<template>
  <v-container fluid>
    <v-card class="pa-4 pt-3">
      <!-- Encabezado -->
      <v-row class="text-center ma-0">
        <v-col cols="1" class="pa-1" />
        <v-col
          v-for="day in weekDays"
          :key="day.label"
          class="text-caption text-grey-darken-1"
        >
          <v-card-subtitle>{{ day.label }}</v-card-subtitle>
          <div class="text-caption">{{ format(day.date, 'dd/MM') }}</div>
        </v-col>
      </v-row>

      <!-- Horas y contenido -->
      <v-row no-gutters>
        <v-col cols="1">
          <div v-for="slot in timeSlots" :key="slot" class="time-slot-label">
            {{ slot }}
          </div>
        </v-col>

        <v-col
          v-for="day in weekDays"
          :key="day.label"
          class="border-left"
          style="border-left: 1px solid #ccc;"
        >
          <div
            v-for="slot in timeSlots"
            :key="slot"
            class="time-slot-cell"
            @click="handleCreate(day.date, slot)"
          >
            <div
              v-for="event in getEventsForDateTime(day.date, slot)"
              :key="event.id"
              class="event-chip"
            >
              <v-chip color="white" size="x-small" class="ma-1">
                {{ event.name }}
              </v-chip>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<script lang="ts">
import { defineComponent, ref, computed, PropType } from 'vue';
import { startOfWeek, addDays, format, setHours, setMinutes, parseISO, isWithinInterval, addMinutes } from 'date-fns';

type CalEvent = {
  id: number;
  date: string;
  name: string;
  original?: any;
};

type CalendarConfig = {
  dateField: string;
  labelField: string;
  colorField?: string;
};

export default defineComponent({
  name: 'CalendarWeek',
  props: {
    baseDate: { type: Date, required: true },
    events: { type: Array as PropType<CalEvent[]>, required: true },
    onCreate: { type: Function as PropType<(data: Record<string, any>) => void>, required: false },
    calendarConfig: { type: Object as PropType<CalendarConfig>, required: true },
  },
  setup(props) {
    const timeStep = ref(30); // minutos entre bloques

    const generateTimeSlots = () => {
      const slots: string[] = [];
      for (let hour = 0; hour < 24; hour++) {
        for (let min = 0; min < 60; min += timeStep.value) {
          const h = hour.toString().padStart(2, '0');
          const m = min.toString().padStart(2, '0');
          slots.push(`${h}:${m}`);
        }
      }
      return slots;
    };

    const timeSlots = computed(() => generateTimeSlots());

    const weekDays = computed(() => {
      const start = startOfWeek(props.baseDate, { weekStartsOn: 1 });
      return Array.from({ length: 7 }, (_, i) => ({
        label: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'][i],
        date: addDays(start, i),
      }));
    });

    function getEventsForDateTime(dayDate: Date, time: string) {
      const [hour, minute] = time.split(':').map(Number);
      const slotStart = setMinutes(setHours(dayDate, hour), minute);
      const slotEnd = addMinutes(slotStart, timeStep.value);

      const dateField = props.calendarConfig?.dateField || 'date';

      return props.events.filter(event => {
        const rawDate = event.original?.[dateField];
        const eventDate = typeof rawDate === 'string' ? parseISO(rawDate) : new Date(rawDate);
        return isWithinInterval(eventDate, { start: slotStart, end: slotEnd });
      });
    }

    function handleCreate(date: Date, time: string) {
      const [hour, minute] = time.split(':').map(Number);
      const fullDate = setMinutes(setHours(date, hour), minute);
      props.onCreate?.({ date: fullDate.toISOString() });
    }

    return {
      // expuesto al template
      format,
      timeSlots,
      weekDays,
      getEventsForDateTime,
      handleCreate,
    };
  },
});
</script>

<style scoped>
.time-slot-label {
  height: 40px;
  padding: 2px;
  font-size: 0.75rem;
  color: #666;
  border-top: 1px solid #eee;
}
.time-slot-cell {
  height: 40px;
  border-top: 1px solid #eee;
  position: relative;
}
.event-chip {
  position: absolute;
  top: 2px;
  left: 2px;
}
</style>
